import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { getAllOrdersHistory, getOrderHistory } from '../../context/CommonContex';
import ActionTypeBadge from './ActionTypeBadge';
import HistoryValueCell from './HistoryValueCell';
import HistoryDetailsCell from './HistoryDetailsCell';
import { getHistoryDisplayValues, resolveStatusActionType, getHistoryDetails, resolveHistoryChangedBy } from './orderHistoryFilter';
import './OrderHistoryModal.css';

const PAGE_SIZE = 1000;

const formatHistoryDate = (isoString) => {
    if (!isoString) return '—';
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
};

const sortByChangedAtDesc = (entries) =>
    [...entries].sort((a, b) => new Date(b.changedAt || 0) - new Date(a.changedAt || 0));

/**
 * @param {string[]|null} [orderNums] - When an array is passed (Order Validation), only those
 *   orders' history is shown. Omit / pass null on the Orders page for the global feed.
 * @param {string} [title]
 */
const AllOrdersHistoryModal = ({
    show,
    onClose,
    orderNums = null,
    title = 'All Orders History',
}) => {
    const [page, setPage] = useState(0);
    const [data, setData] = useState({ content: [], totalElements: 0, totalPages: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    /** Full merged list when scoped to specific order numbers (client-side pagination). */
    const [scopedEntries, setScopedEntries] = useState(null);

    const isScoped = Array.isArray(orderNums);
    const scopedOrderNums = useMemo(() => {
        if (!isScoped) return null;
        return [...new Set(orderNums.map((n) => String(n).trim()).filter(Boolean))];
    }, [isScoped, orderNums]);

    const applyScopedPage = useCallback((entries, pageToFetch) => {
        const totalElements = entries.length;
        const totalPages = totalElements === 0 ? 0 : Math.ceil(totalElements / PAGE_SIZE);
        const safePage = Math.min(pageToFetch, Math.max(totalPages - 1, 0));
        const start = safePage * PAGE_SIZE;
        setData({
            content: entries.slice(start, start + PAGE_SIZE),
            totalElements,
            totalPages,
        });
        setPage(safePage);
    }, []);

    const fetchGlobalPage = useCallback(async (pageToFetch) => {
        setLoading(true);
        setError(false);
        try {
            const res = await getAllOrdersHistory(pageToFetch, PAGE_SIZE);
            setData(res && Array.isArray(res.content) ? res : { content: [], totalElements: 0, totalPages: 0 });
            setPage(pageToFetch);
        } catch (err) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchScopedHistory = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            if (!scopedOrderNums || scopedOrderNums.length === 0) {
                setScopedEntries([]);
                applyScopedPage([], 0);
                return;
            }

            const results = await Promise.all(
                scopedOrderNums.map(async (ordernum) => {
                    try {
                        const entries = await getOrderHistory(ordernum);
                        return Array.isArray(entries) ? entries : [];
                    } catch (err) {
                        return [];
                    }
                })
            );

            const merged = sortByChangedAtDesc(results.flat());
            setScopedEntries(merged);
            applyScopedPage(merged, 0);
        } catch (err) {
            setError(true);
            setScopedEntries([]);
        } finally {
            setLoading(false);
        }
    }, [scopedOrderNums, applyScopedPage]);

    useEffect(() => {
        if (show) {
            setPage(0);
            setScopedEntries(null);
            if (isScoped) {
                fetchScopedHistory();
            } else {
                fetchGlobalPage(0);
            }
        } else {
            setData({ content: [], totalElements: 0, totalPages: 0 });
            setScopedEntries(null);
        }
    }, [show, isScoped, fetchScopedHistory, fetchGlobalPage]);

    const goToPage = (nextPage) => {
        if (scopedEntries) {
            applyScopedPage(scopedEntries, nextPage);
            return;
        }
        fetchGlobalPage(nextPage);
    };

    return (
        <Modal show={show} onHide={onClose} centered dialogClassName="order-history-modal-dialog" enforceFocus={false}>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: '18px' }}>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {loading && (
                    <div className="text-center py-4">
                        <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                        <div className="mt-2 text-muted">Loading history…</div>
                    </div>
                )}
                {!loading && error && (
                    <div className="text-center text-danger py-4">Failed to load history. Please try again.</div>
                )}
                {!loading && !error && data.content.length === 0 && (
                    <div className="text-center text-muted py-4">
                        {isScoped
                            ? 'No history recorded for orders on Order Validation yet.'
                            : 'No history recorded yet.'}
                    </div>
                )}
                {!loading && !error && data.content.length > 0 && (
                    <>
                        <table className="table table-sm table-hover align-middle mb-0 order-history-table all-orders-history-table">
                            <colgroup>
                                <col className="oh-col-order" />
                                <col className="oh-col-date" />
                                <col className="oh-col-action" />
                                <col className="oh-col-details" />
                                <col className="oh-col-prev" />
                                <col className="oh-col-new" />
                                <col className="oh-col-user" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>Order #</th>
                                    <th className="oh-col-date">Date &amp; Time</th>
                                    <th className="oh-col-action">Action</th>
                                    <th>Details</th>
                                    <th>Previous Value</th>
                                    <th>New Value</th>
                                    <th>Changed By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.content.map((entry) => {
                                    const { previousValue, newValue } = getHistoryDisplayValues(entry);
                                    return (
                                        <tr key={entry.id}>
                                            <td className="oh-nowrap">{entry.ordernum || '—'}</td>
                                            <td className="oh-nowrap oh-col-date">{formatHistoryDate(entry.changedAt)}</td>
                                            <td className="oh-col-action"><ActionTypeBadge actionType={resolveStatusActionType(entry)} actionDetail={entry.actionDetail} /></td>
                                            <td className="oh-col-details"><HistoryDetailsCell text={getHistoryDetails(entry)} /></td>
                                            <td><HistoryValueCell value={previousValue} /></td>
                                            <td><HistoryValueCell value={newValue} /></td>
                                            <td className="oh-nowrap">{resolveHistoryChangedBy(entry)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div className="d-flex align-items-center justify-content-between mt-3">
                            <span className="text-muted small">
                                Page {data.totalPages === 0 ? 0 : page + 1} of {data.totalPages} ({data.totalElements} records)
                            </span>
                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={page <= 0}
                                    onClick={() => goToPage(page - 1)}
                                >
                                    Previous
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={page + 1 >= data.totalPages}
                                    onClick={() => goToPage(page + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default AllOrdersHistoryModal;
